import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { get, set, cloneDeep } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Pluralize from 'react-pluralize';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import {
  SplitButton,
  MenuItem,
  ButtonGroup,
  Button,
} from 'react-bootstrap/lib';
import { PenProtectionPlanSourceTypes } from './ProtectionPlansConstants';
import penActions from '../../redux/pen/actions';
import dealerActions from '../../redux/dealers/actions';
import ModifyProductModal from './ModifyProductModal';
import DefaultNewProduct from './DefaultNewProduct';
import LoadingOverlay from '../LoadingOverlay';
import HideProductPriceChallenge from './hideProductPriceChallengeModal';
import ModifyProductOrder from './ModifyProductOrder';
import { selectionTypeInStore, selectionTypeOnline } from './cellFormatters';

function ProtectionPlansPenProducts(props) {
  const { pen, actions, dealer, fetching } = props;

  const [isEditingOrder, setIsEditingOrder] = useState();

  useEffect(() => {
    actions.getProvidersRequest();
    if (dealer && dealer.penConfig) {
      pen.penConfig.hideProductPrices = dealer.penConfig.hideProductPrices;
    }
  }, []);

  const editProduct = (product) => {
    ModifyProductModal({
      product,
      dealer,
    });
  };

  const createProduct = (productType) => {
    const product = cloneDeep(DefaultNewProduct);

    product.source = productType;
    if (productType === PenProtectionPlanSourceTypes.USER) {
      product.raw.name = 'N/A';
    }

    product.description = [];

    ModifyProductModal({
      product,
      dealer,
    });
  };

  const productAction = (_cell, row) => (
    <button
      className="btn btn-primary"
      onClick={() => {
        editProduct(row);
      }}
    >
      Edit
    </button>
  );

  const processProducts = (products, filter) => {
    let tmpProducts = cloneDeep(products);
    if (filter !== null) {
      tmpProducts = tmpProducts.filter((product) => product.enabled === filter);
    }
    return tmpProducts.filter((product) => product);
  };

  const findEnabledPenProducts = useCallback(
    () =>
      pen.penConfig.products.filter(
        (p) => p.enabled && p.source === PenProtectionPlanSourceTypes.PROVIDER,
      ) || [],
    [pen],
  );

  const [products, setProducts] = useState(
    processProducts(dealer.penProducts || [], pen.penConfig.productListFilter),
  );

  useEffect(() => {
    setProducts(
      processProducts(
        dealer.penProducts || [],
        pen.penConfig.productListFilter,
      ),
    );
  }, [dealer, pen.penConfig.productListFilter]);

  const [enabledPenProducts, setEnabledPenProducts] = useState(
    findEnabledPenProducts,
  );

  useEffect(() => {
    setEnabledPenProducts(findEnabledPenProducts);
  }, [findEnabledPenProducts, pen]);

  const showName = (raw) => (raw && raw.name ? raw.name : '');

  const onEditOrder = useCallback(() => setIsEditingOrder(true), []);

  const showProviderName = (raw) => {
    let provider;
    if (pen.allProviders) {
      provider = pen.allProviders.find(
        (p) => p && raw && raw.providerId === p.id,
      );
    }
    return provider && provider.name ? provider.name : 'N/A';
  };

  const toggleShowDailyPrice = () => {
    const { penConfig } = pen;
    set(penConfig, 'showDailyPrice', !penConfig?.showDailyPrice);
    actions.setPenConfigRequest({ penConfig });
  };

  const enableProductsInUIToggle = () => {
    const { penConfig } = pen;
    set(penConfig, 'enableInUI.products', !penConfig.enableInUI.products);
    actions.setPenConfigRequest({ penConfig });
  };

  const showTotalCostUIToggle = () => {
    const { penConfig } = pen;
    set(penConfig, 'showTotalCost', !penConfig.showTotalCost);
    actions.setPenConfigRequest({ penConfig });
  };

  const useSelectedOfferTermsAndMilesUIToggle = () => {
    const { penConfig } = pen;
    set(
      penConfig,
      'useSelectedOfferTermsAndMiles',
      !penConfig.useSelectedOfferTermsAndMiles,
    );
    actions.setPenConfigRequest({ penConfig });
  };

  const hideProductPricesUIToggle = () => {
    const { penConfig } = pen;

    if (enabledPenProducts.length > 0 && !get(penConfig, 'hideProductPrices')) {
      // If PEN Products are enabled && Hide Product Prices
      // was not previously checked
      // Challenge the Users life choices
      HideProductPriceChallenge({
        pen,
        dealer,
        enabledPenProducts,
      }).then((confirmChange) => {
        if (confirmChange) {
          // User has Disabled PEN products
          // Save their choice
          set(penConfig, 'hideProductPrices', true);
          actions.setPenConfigRequest({ penConfig });
        }
      });
    } else {
      // Just switch the toggle already
      set(penConfig, 'hideProductPrices', !penConfig.hideProductPrices);
      actions.setPenConfigRequest({ penConfig });
    }
  };

  const filterProducts = (enabled) => {
    setProducts(processProducts(dealer.penProducts, enabled));
    pen.penConfig.productListFilter = enabled;
    actions.setPenConfigRequest({ penConfig: pen.penConfig });
  };

  if (isEditingOrder) {
    return (
      <ModifyProductOrder
        products={dealer.penProducts}
        fetching={fetching}
        onClose={() => setIsEditingOrder(false)}
        updateDealerProductsOrder={actions.updateDealerRequest}
        dealer={dealer}
      />
    );
  }

  return (
    <LoadingOverlay active={fetching}>
      <div>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group fancy-checkbox">
              <input
                type="checkbox"
                name="allowIndividualProductPurchase"
                id="allowIndividualProductPurchase"
                autoComplete="off"
                value={get(pen, 'penConfig.enableInUI.products', true)}
                checked={get(pen, 'penConfig.enableInUI.products', true)}
                onChange={() => enableProductsInUIToggle()}
              />
              <div className="btn-group">
                <label
                  htmlFor="allowIndividualProductPurchase"
                  className="btn btn-primary"
                >
                  <span className="glyphicon glyphicon-ok" />
                  {/* second empty span required for checked / not checked styles */}
                  <span />
                </label>
                <label
                  htmlFor="allowIndividualProductPurchase"
                  className="btn btn-default active"
                >
                  Allow Individual Product Purchases
                </label>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group fancy-checkbox">
              <input
                type="checkbox"
                name="showDailyPrice"
                id="showDailyPrice"
                autoComplete="off"
                value={get(pen, 'penConfig.showDailyPrice', false)}
                checked={get(pen, 'penConfig.showDailyPrice', false)}
                onChange={() => toggleShowDailyPrice()}
              />
              <div className="btn-group">
                <label htmlFor="showDailyPrice" className="btn btn-primary">
                  <span className="glyphicon glyphicon-ok" />
                  {/* second empty span required for checked / not checked styles */}
                  <span />
                </label>
                <label
                  htmlFor="showDailyPrice"
                  className="btn btn-default active"
                >
                  Show Product Prices Per Day
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="row margin-top-10 margin-bottom-10">
          <div className="col-md-2">
            <label>
              <input
                type="checkbox"
                name="showTotalCost"
                id="showTotalCost"
                value={get(pen, 'penConfig.showTotalCost', false)}
                checked={get(pen, 'penConfig.showTotalCost', false)}
                onChange={showTotalCostUIToggle}
              />
              <span>Show Total Cost</span>
            </label>
          </div>
          <div className="col-md-2">
            <input
              type="checkbox"
              name="hideProductPrices"
              id="hideProductPrices"
              value={get(pen, 'penConfig.hideProductPrices', false)}
              checked={get(pen, 'penConfig.hideProductPrices', false)}
              onChange={() => hideProductPricesUIToggle()}
            />
            <label htmlFor="hideProductPrices">Hide Product Prices</label>
          </div>
          <div className="col-md-3">
            <label>
              <input
                type="checkbox"
                name="useSelectedOfferTermsAndMiles"
                id="useSelectedOfferTermsAndMiles"
                value={get(
                  pen,
                  'penConfig.useSelectedOfferTermsAndMiles',
                  false,
                )}
                checked={get(
                  pen,
                  'penConfig.useSelectedOfferTermsAndMiles',
                  false,
                )}
                onChange={useSelectedOfferTermsAndMilesUIToggle}
              />
              <span>Use Selected Offer&apos;s Terms and Miles</span>
            </label>
          </div>
        </div>

        <div className="row table-controls-container">
          <div className="col-md-4">
            <h3>
              <Pluralize
                singular="Product"
                plural="Products"
                count={products.length}
              />
            </h3>
            <SplitButton
              title="Add Static Product"
              id={`add-static-product-button`}
              onClick={() => {
                createProduct(PenProtectionPlanSourceTypes.USER);
              }}
            >
              <MenuItem
                id={`add-PEN-product-button`}
                onClick={() => {
                  createProduct(PenProtectionPlanSourceTypes.PROVIDER);
                }}
              >
                Add PEN Product
              </MenuItem>
            </SplitButton>
          </div>
          <div className="col-md-4 button-group-container">
            <label className="btn button-group-label">Filters</label>
            <ButtonGroup aria-label="Product Filter">
              <Button
                onClick={() => filterProducts(null)}
                className={
                  pen.penConfig.productListFilter === null ? 'selected' : ''
                }
              >
                All Products
              </Button>{' '}
              <Button
                onClick={() => filterProducts(true)}
                className={
                  pen.penConfig.productListFilter === true ? 'selected' : ''
                }
              >
                Enabled
              </Button>{' '}
              <Button
                onClick={() => filterProducts(false)}
                className={
                  pen.penConfig.productListFilter === false ? 'selected' : ''
                }
              >
                Disabled
              </Button>
            </ButtonGroup>
          </div>
          <div className="col-md-4 product-edit-order-button-container">
            <button className="btn btn-primary" onClick={onEditOrder}>
              Edit Order
            </button>
          </div>
        </div>
        <BootstrapTable
          data={products}
          striped
          hover
          bodyContainerClass="list-body-container"
          tableStyle={{ border: 'none' }}
        >
          <TableHeaderColumn
            dataField="raw"
            dataSort
            isKey
            dataFormat={showName}
          >
            Name
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="raw"
            dataSort
            dataFormat={showProviderName}
          >
            Provider
          </TableHeaderColumn>
          <TableHeaderColumn dataField="displayName" dataSort>
            Display Name
          </TableHeaderColumn>
          <TableHeaderColumn dataField="enabled" dataSort>
            Enabled
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="selectedInStore"
            width="145"
            dataSort
            dataFormat={selectionTypeInStore}
          >
            In-Store selection
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="selectedOnline"
            dataSort
            dataFormat={selectionTypeOnline}
          >
            Online selection
          </TableHeaderColumn>
          <TableHeaderColumn dataField="action" dataFormat={productAction}>
            Actions
          </TableHeaderColumn>
        </BootstrapTable>
      </div>
    </LoadingOverlay>
  );
}

ProtectionPlansPenProducts.propTypes = {
  pen: PropTypes.object,
  actions: PropTypes.object,
  dealer: PropTypes.object,
  fetching: PropTypes.bool,
};

const mapStateToProps = ({ pen }) => ({
  pen,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...penActions,
      ...dealerActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProtectionPlansPenProducts);
